import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import AcademicAchievement from './AcademicAchievement';
import AcademicGrowth from './AcademicGrowth';
import SchoolQuality from './SchoolQuality';
import Telpas from './Telpas';
import Ccmr from './Ccmr';
import GradRate from './GradRate';
import { Button, Grid, Tooltip, IconButton } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { tagColumnsToIndicators } from '../Common/utils';
const isDemo = process.env.REACT_APP_THEME === 'demo';
const ComponentNames = {
  academicAchievement: 'academicAchievement',
  academicGrowth: 'academicGrowth',
  gradRate: 'gradRate',
  telpas: 'telpas',
  schoolQuality: 'schoolQuality',
  ccmr: 'ccmr',
  participation: 'participation',
};

const styles = (theme) => ({
  root: {},
  menu: {},
  textField: {},
  formControl: {
    display: 'block',
  },
  finalTable: {
    width: 300,
  },
  boldCell: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
});

class D3 extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    report: PropTypes.object.isRequired,
    handleGetStudentsList: PropTypes.func,
    profile: PropTypes.object,
    showTenths: PropTypes.bool,
  };
  state = {
    showAllTargets: false,
    showAdditionalStudentGroups: false,
  };

  handleShowAllTargetsChange(val) {
    this.setState({ showAllTargets: val });
  }
  handleShowAdditionalStudentGroupsChange(val) {
    this.setState({ showAdditionalStudentGroups: val });
  }

  // Demo: frame each Domain-3 section as its own titled card with spacing + a color accent.
  renderSection(title, accent, content) {
    if (!isDemo) return content;
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 8px 28px rgba(58,166,185,0.12)',
          border: '1px solid rgba(191,247,255,0.6)',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 22px',
            borderBottom: '1px solid #e4eef0',
            background: 'linear-gradient(180deg,#f1fcfe,#ffffff)',
          }}
        >
          <span style={{ width: 12, height: 12, borderRadius: 4, background: accent, marginRight: 10 }} />
          <span style={{ fontWeight: 700, fontSize: '1rem', color: '#2c3e40' }}>{title}</span>
        </div>
        <div style={{ overflowX: 'auto', padding: '4px 6px 8px' }}>{content}</div>
      </div>
    );
  }

  render() {
    const { report, profile, handleGetStudentsList, studentsListPath, showTenths } = this.props;
    const { tables } = report.d3;
    const d2aTables = report && report.d2a && report.d2a.tables ? report.d2a.tables : null;
    const tagColumns = tagColumnsToIndicators(report.columnTags);
    const { schoolTypeConversion } = report;
    const { showAllTargets, showAdditionalStudentGroups } = this.state;

    const sortTables = Object.entries(tables);
    sortTables.sort((a, b) => {
      if (a[0] === ComponentNames.participation) {
        return -1;
      }
      if (b[0] === ComponentNames.participation) {
        return -1;
      }
      return 1;
    });
    const d3Summary = report && report.d3 ? report.d3 : {};
    const d3Scaled = d3Summary.scaled;
    const d3Letter = d3Summary.letter;
    const d3Raw = d3Summary.raw;
    return (
      <div id="reportD3">
        {isDemo && (d3Scaled != null || d3Letter != null || d3Raw != null) && (
          <div
            style={{
              background: '#fff',
              padding: '18px 22px',
              borderRadius: 16,
              boxShadow: '0 6px 20px rgba(58,166,185,0.10)',
              border: '1px solid rgba(191,247,255,0.6)',
              marginBottom: 22,
              display: 'flex',
              alignItems: 'center',
              gap: 18,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(135deg,#BFF7FF,#3aa6b9)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {d3Letter != null && d3Letter !== '' ? d3Letter : '–'}
            </div>
            <div style={{ flex: '1 1 auto' }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e40' }}>Closing the Gaps</div>
              <div style={{ fontSize: '0.82rem', color: '#7c8a8d', marginTop: 2 }}>Domain 3 overall</div>
            </div>
            {d3Scaled != null && d3Scaled !== '' && (
              <div style={{ textAlign: 'center', padding: '0 14px' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2c3e40' }}>{d3Scaled}</div>
                <div style={{ fontSize: '0.82rem', color: '#7c8a8d', marginTop: 2 }}>Scaled score</div>
              </div>
            )}
            {d3Raw != null && d3Raw !== '' && (
              <div style={{ textAlign: 'center', padding: '0 14px' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2c3e40' }}>{d3Raw}</div>
                <div style={{ fontSize: '0.82rem', color: '#7c8a8d', marginTop: 2 }}>Raw score</div>
              </div>
            )}
          </div>
        )}
        <Grid container direction="row" justify="center" alignItems="center">
          <Button variant="contained" onClick={(e) => this.handleShowAllTargetsChange(!showAllTargets)}>
            {showAllTargets ? 'Hide Invalid Targets' : 'Show All Targets'}
            <Tooltip
              title='Due to the nature of how some targets are variable based on prior year performance while others are fixed
            based on statewide constants, sometimes a 1-point or even a 2-point target is greater than the 3-point or
            4-point target in the same indicator. In these cases, we hide these invalid targets that are "out of order"
            to avoid confusion. For example, if the 3-point target is 80 and the 2-point target is 82, we hide the 82,
            because if the school earns 82 points in the indicator, they would be awarded the full 3 points.'
              placement="bottom-end"
              enterDelay={300}
            >
              <InfoIcon style={{ float: 'right' }} />
            </Tooltip>
          </Button>
        </Grid>
        <br />

        <Grid container direction="row" justify="center" alignItems="center">
          <Button
            variant="contained"
            onClick={(e) => this.handleShowAdditionalStudentGroupsChange(!showAdditionalStudentGroups)}
          >
            {showAdditionalStudentGroups ? 'Hide ADDITIONAL STUDENT GROUPS' : 'SHOW ADDITIONAL STUDENT GROUPS'}
            <Tooltip title="ADDITIONAL STUDENT GROUPS" placement="bottom-end" enterDelay={300}>
              <InfoIcon style={{ float: 'right' }} />
            </Tooltip>
          </Button>
        </Grid>

        {tables.academicAchievement &&
          this.renderSection(
            'Academic Achievement',
            '#F0CA4D',
            <AcademicAchievement
              component={tables.academicAchievement}
              studentsListPath={`${profile.uid}/d1/staar`}
              handleGetStudentsList={handleGetStudentsList}
              profile={profile}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
              tagColumns={tagColumns}
            />
          )}
        {tables.academicGrowth &&
          this.renderSection(
            'Academic Growth',
            '#92C83E',
            <AcademicGrowth
              d2aTables={d2aTables}
              component={tables.academicGrowth}
              studentsListPath={`${profile.uid}/d2a`}
              handleGetStudentsList={handleGetStudentsList}
              profile={profile}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
              tagColumns={tagColumns}
            />
          )}
        {tables.gradRate &&
          this.renderSection(
            'Graduation Rate',
            '#5CBAF3',
            <GradRate
              component={tables.gradRate}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
            />
          )}
        {tables.telpas &&
          this.renderSection(
            'English Language Proficiency',
            '#00ACBB',
            <Telpas
              component={tables.telpas}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
            />
          )}
        {tables.schoolQuality &&
          this.renderSection(
            'Student Success',
            '#F9801D',
            <SchoolQuality
              component={tables.schoolQuality}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
            />
          )}
        {tables.ccmr &&
          this.renderSection(
            'CCMR',
            '#A21FFF',
            <Ccmr
              component={tables.ccmr}
              schoolTypeConversion={schoolTypeConversion}
              showAllTargets={showAllTargets}
              showTenths={showTenths}
              showAdditionalStudentGroups={showAdditionalStudentGroups}
              tagColumns={tagColumns}
            />
          )}
        {/* {
          tables.participation && (
            <Participation component={tables.participation} />
          )
        } */}
      </div>
    );
  }
}

export default withStyles(styles)(D3);
